import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingStudentSummaryControls } from 'teacher-ui-client/components/reporting/student-summary-controls'
import { t, type IntlService } from 'ember-intl'
import { array } from '@ember/helper'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { not } from 'ember-truth-helpers'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import { ReportingNoDataMessageIf } from 'teacher-ui-client/components/reporting/no-data-message-if'
import { StudentChartWrapper } from 'teacher-ui-client/components/student-chart-wrapper'
import { CourseProgressStudent } from 'teacher-ui-client/components/giraffe/charts/course-progress/student'
import { QuizScoresStudent } from 'teacher-ui-client/components/giraffe/charts/quiz-scores/student'
import { LexileDetailsStudent } from 'teacher-ui-client/components/giraffe/charts/lexile-details/student'
import { AssessmentScoresStudentByGenre } from 'teacher-ui-client/components/giraffe/charts/assessment-scores/student-by-genre'
import { BooksReadStudent } from 'teacher-ui-client/components/giraffe/charts/books-read/student'
import { StadiumEventsStudent } from 'teacher-ui-client/components/giraffe/charts/stadium-events/student'
import type UiScopeRexStudentSummaryRoute from 'teacher-ui-client/reporting/ui-scope/rex/student-summary/index/route'
import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { isPresent } from '@ember/utils'
import { service } from '@ember/service'

interface Signature {
  Args: {
    model: ModelFor<UiScopeRexStudentSummaryRoute>
  }
}
export class UiScopeRexStudentSummaryIndexPage extends Component<Signature> {
  @service intl!: IntlService

  get pageTitle() {
    const studentName = this.args.model.student && this.args.model.student.fullName
    return this.intl.t('reporting.rex.studentSummary.pageTitle', { name: studentName })
  }

  get anyDataCheck() {
    const {
      students,
      quizScoresStudent,
      lexileDetailsStudent,
      assessmentScoresStudent,
      booksReadStudent,
      courseProgressSpelling,
      stadiumEventsStudent,
    } = this.args.model
    const studentsAndAnyDataPresent =
      students &&
      [
        quizScoresStudent,
        lexileDetailsStudent,
        assessmentScoresStudent,
        booksReadStudent,
        courseProgressSpelling,
        stadiumEventsStudent,
      ].some(isPresent)
    return studentsAndAnyDataPresent
  }

  <template>
    <ReportingTeacherUiHeader @title={{this.pageTitle}} />

    <ReportingStudentSummaryControls
      @students={{@model.students}}
      @student={{@model.student}}
      @chartRoute="reporting.ui-scope.rex.student-summary"
      @tableRoute="reporting.ui-scope.rex.dashboard.student"
      @exportOptions={{array ExportType.print}}
      @isDataPresent={{this.anyDataCheck}}
      @studentRouteName="reporting.ui-scope.rex.student-summary"
      @allStudentsRouteName="reporting.ui-scope.rex.dashboard"
    />

    <ReportingNoDataMessageIf @on={{not @model.students}}>
      <StudentChartWrapper @empty={{not @model.courseProgressComprehension}}>
        {{#if @model.courseProgressComprehension}}
          <CourseProgressStudent
            @data={{@model.courseProgressComprehension}}
            @precinct={{Precinct.REX_READING}}
            @period={{@model.period}}
            @summaryTitle={{t "reporting.summaryTitles.courseProgressStudent"}}
          />
        {{/if}}
      </StudentChartWrapper>

      <StudentChartWrapper @empty={{not @model.quizScoresStudent}}>
        {{#if @model.quizScoresStudent}}
          <QuizScoresStudent
            data-test-charts-quiz-scores-student
            @data={{@model.quizScoresStudent}}
            @summaryTitle={{t "reporting.summaryTitles.quizScoresStudent"}}
          />
        {{/if}}
      </StudentChartWrapper>

      <StudentChartWrapper @empty={{not @model.lexileDetailsStudent}}>
        {{#if @model.lexileDetailsStudent}}
          <LexileDetailsStudent @data={{@model.lexileDetailsStudent}} @product={{@model.product}} />
        {{/if}}
      </StudentChartWrapper>

      <StudentChartWrapper @empty={{not @model.assessmentScoresStudent}}>
        {{#if @model.assessmentScoresStudent}}
          <AssessmentScoresStudentByGenre
            @data={{@model.assessmentScoresStudent}}
            @summaryTitle={{t "reporting.summaryTitles.assessmentScoresStudent.title.rex"}}
          />
        {{/if}}
      </StudentChartWrapper>

      <StudentChartWrapper @empty={{not @model.booksReadStudent}}>
        {{#if @model.booksReadStudent}}
          <BooksReadStudent
            @data={{@model.booksReadStudent}}
            @product={{@model.product}}
            @period={{@model.period}}
            @summaryTitle={{t "reporting.summaryTitles.booksReadStudent"}}
          />
        {{/if}}
      </StudentChartWrapper>

      <div class="course-progress-spelling">
        <StudentChartWrapper @empty={{not @model.courseProgressSpelling}}>
          {{#if @model.courseProgressSpelling}}
            <CourseProgressStudent
              @data={{@model.courseProgressSpelling}}
              @precinct={{Precinct.REX_SPELLING}}
              @summaryTitle={{t "reporting.summaryTitles.courseProgressSpelling"}}
              @period={{@model.period}}
            />
          {{/if}}
        </StudentChartWrapper>
      </div>

      <StudentChartWrapper @empty={{not @model.stadiumEventsStudent}}>
        {{#if @model.stadiumEventsStudent}}
          <StadiumEventsStudent
            @data={{@model.stadiumEventsStudent}}
            @summaryTitle={{t "reporting.summaryTitles.stadiumEventsStudent"}}
          />
        {{/if}}
      </StudentChartWrapper>
    </ReportingNoDataMessageIf>
  </template>
}

export default RouteTemplate(UiScopeRexStudentSummaryIndexPage)
